import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  Building2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  History,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Package,
  Receipt,
  Settings,
  ShieldAlert,
  ShieldX,
  Sun,
} from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { useTheme } from "@/lib/theme.jsx";
import {
  useSidebarCollapsed,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "@/lib/useSidebarCollapsed";
import { useAutoUpdateCheck } from "@/lib/useAutoUpdateCheck";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { HelpModeToggle } from "@/lib/help.jsx";
import { EngineBadge } from "@/components/EngineBadge";
import { SetupGuideButton } from "@/components/OnboardingWizard";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const NAV = [
  { to: "/platform", key: "platformOverview", icon: LayoutDashboard, testid: "pnav-overview" },
  { to: "/platform/tenants", key: "platformTenants", icon: Building2, testid: "pnav-tenants" },
  { to: "/platform/plans", key: "platformPlans", icon: CreditCard, testid: "pnav-plans" },
  { to: "/platform/billing", key: "platformBilling", icon: Receipt, testid: "pnav-billing" },
  { to: "/platform/firewall", key: "firewall", icon: ShieldX, testid: "pnav-firewall" },
  { to: "/platform/audit", key: "auditLog", icon: History, testid: "pnav-audit" },
  { to: "/platform/security", key: "platformSecurity", icon: ShieldAlert, testid: "pnav-security" },
  { to: "/platform/system", key: "platformSystem", icon: Package, testid: "pnav-system" },
  { to: "/platform/settings", key: "platformSettings", icon: Settings, testid: "pnav-settings" },
];

/** Phase O+ — release stamp shown at the very bottom of the sidebar.
 * Uses the public `/api/version` endpoint. Silent fallback when
 * unreachable. */
function PlatformVersionFooter({ collapsed }) {
  const [stamp, setStamp] = useState("");
  useEffect(() => {
    let alive = true;
    api
      .get("/version")
      .then((r) => alive && setStamp(r.data?.current || ""))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!stamp) return null;
  if (collapsed) {
    return (
      <div
        className="border-t border-border py-1.5 text-center text-[9px] font-mono text-muted-foreground truncate"
        title={stamp}
        data-testid="platform-version-footer-collapsed"
      >
        {stamp.replace(/^v/, "").split("-")[0].slice(0, 6) || "dev"}
      </div>
    );
  }
  return (
    <div
      className="border-t border-border px-5 py-2 text-[10px] font-mono text-muted-foreground truncate"
      title={stamp}
      data-testid="platform-version-footer"
    >
      {stamp}
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
  const { user, setUser } = useAuth();
  const [oldp, setOldp] = useState("");
  const [newp, setNewp] = useState("");
  const [newp2, setNewp2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newp !== newp2) return toast.error("Passwords do not match");
    if (newp.length < 8) return toast.error("New password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldp,
        new_password: newp,
      });
      toast.success("Password changed");
      setOldp("");
      setNewp("");
      setNewp2("");
      setUser({ ...user, must_change_password: false });
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // When forced, don't let the user dismiss without completing
        if (forced && !v) return;
        onOpenChange(v);
      }}
    >
      <DialogContent data-testid="platform-change-password-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[hsl(var(--security))]" />
            {forced ? "Set a new password" : "Change password"}
          </DialogTitle>
          {forced && (
            <p className="text-xs text-muted-foreground pt-1">
              You must set a new password before continuing. The default
              credentials are not allowed in production.
            </p>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" autoComplete="off">
          <div>
            <Label htmlFor="pp-old">Current password</Label>
            <PasswordInput
              id="pp-old"
              value={oldp}
              onChange={(e) => setOldp(e.target.value)}
              data-testid="platform-pw-old"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <Label htmlFor="pp-new">New password</Label>
            <PasswordInput
              id="pp-new"
              value={newp}
              onChange={(e) => setNewp(e.target.value)}
              data-testid="platform-pw-new"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <div>
            <Label htmlFor="pp-new2">Confirm new password</Label>
            <PasswordInput
              id="pp-new2"
              value={newp2}
              onChange={(e) => setNewp2(e.target.value)}
              data-testid="platform-pw-new2"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <DialogFooter>
            {!forced && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={busy} data-testid="platform-pw-submit">
              {busy ? "Saving…" : "Save new password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PlatformShell({ children }) {
  const { username, user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const loc = useLocation();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed(false);
  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  // Force-open the dialog when the server flagged must_change_password
  const forced = Boolean(user?.must_change_password);
  const [pwOpen, setPwOpen] = useState(forced);
  // Phase N+ — mobile sheet (PlatformShell did not have one before).
  const [sheetOpen, setSheetOpen] = useState(false);

  // Hotfix Z.20 — auto-poll the release manifest every hour so operators
  // don't have to remember to check for hotfixes. The hook itself
  // handles first-detection toast + localStorage de-dup; here we just
  // read `updateAvailable` to render the header pill.
  const { latest: autoLatest, updateAvailable: autoUpdateAvail } =
    useAutoUpdateCheck();

  const renderNavItem = (it, opts = {}) => {
    const Icon = it.icon;
    const active = loc.pathname === it.to;
    const onMobile = opts.onMobile === true;
    const showAsCollapsed = !onMobile && collapsed;
    const linkClass = `flex items-center gap-3 rounded-md text-sm transition-colors ${
      showAsCollapsed ? "px-2 py-2 justify-center" : "px-3 py-2"
    } ${
      active
        ? "nav-active"
        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
    }`;
    const label = t(`nav.${it.key}`);
    const link = (
      <Link
        to={it.to}
        data-testid={it.testid}
        className={linkClass}
        onClick={() => onMobile && setSheetOpen(false)}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!showAsCollapsed && <span>{label}</span>}
      </Link>
    );
    return showAsCollapsed ? (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="text-xs">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      link
    );
  };

  // The actual contents of the sidebar — reused by the desktop <aside> AND
  // by the mobile <Sheet> drawer so we have ONE source of truth.
  const sidebarBody = ({ onMobile = false } = {}) => {
    const showAsCollapsed = !onMobile && collapsed;
    return (
      <div className="flex flex-col h-full">
        <div className={`${showAsCollapsed ? "px-2" : "px-5"} py-5 border-b border-border`}>
          <div className={`flex items-center ${showAsCollapsed ? "justify-center" : "gap-2"}`}>
            <div className="h-9 w-9 rounded-md bg-[hsl(var(--security))]/15 grid place-items-center shrink-0">
              <Activity className="h-5 w-5 text-[hsl(var(--security))]" />
            </div>
            {!showAsCollapsed && (
              <div>
                <div className="font-semibold tracking-tight text-foreground">
                  {t("common.appName")}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--security))]">
                  Platform Admin
                </div>
              </div>
            )}
          </div>
        </div>
        {!showAsCollapsed && (
          <div
            className="px-5 pt-4 pb-2 text-[12px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--security))]"
            data-testid="platform-universe-header"
          >
            Super Admin
          </div>
        )}
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          <ul className="space-y-0.5">
            {NAV.map((it) => (
              <li key={it.to}>{renderNavItem(it, { onMobile })}</li>
            ))}
          </ul>
        </nav>
        <PlatformVersionFooter collapsed={showAsCollapsed} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside
        className="hidden md:flex shrink-0 border-r border-border bg-card sticky top-0 h-screen flex-col relative transition-[width] duration-200 ease-out"
        style={{ width: sidebarWidth }}
        data-testid="platform-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
      >
        {sidebarBody({ onMobile: false })}
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="platform-sidebar-toggle"
          aria-label={collapsed
            ? t("nav.expandSidebar", "Expand sidebar")
            : t("nav.collapseSidebar", "Collapse sidebar")}
          className="absolute -right-3 top-20 z-20 h-6 w-6 rounded-full border border-border bg-card shadow grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          {collapsed
            ? <ChevronRight className="h-3.5 w-3.5" />
            : <ChevronLeft  className="h-3.5 w-3.5" />}
        </button>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-[var(--toolbar-h)] border-b border-border bg-background/95 backdrop-blur flex items-center gap-1 sm:gap-2 md:gap-3 px-2 sm:px-4 md:px-6 flex-nowrap overflow-hidden">
          {/* Mobile-only hamburger trigger. Hidden at md+ where the sidebar
              is rendered inline. */}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden shrink-0"
                data-testid="platform-open-mobile-nav"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px]">
              {sidebarBody({ onMobile: true })}
            </SheetContent>
          </Sheet>
          {/* Tagline — single line on tiny screens, truncates to make
              room for the always-visible right-side controls (Hotfix Z.8). */}
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[hsl(var(--security))] font-semibold whitespace-nowrap truncate min-w-0 flex-1">
            <span className="hidden lg:inline">Platform Super-Admin</span>
            <span className="lg:hidden">Admin</span>
          </div>
          {forced && (
            <span
              data-testid="platform-must-change-pw-badge"
              className="hidden xl:inline-block ml-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-destructive/50 text-destructive bg-destructive/5 whitespace-nowrap shrink-0"
            >
              Change default password
            </span>
          )}
          {/* Phase 6an (Hotfix Z.7) — engine health pill, clickable for
              probe diagnostics when not "up". Compact form (dot + label
              only) at all widths; verbose host:port appears at xl+. */}
          <div className="hidden md:block shrink-0">
            <EngineBadge />
          </div>
          {/* Hotfix Z.20 — "Update available" pill. Rendered only when
              the auto-poll has discovered a newer release than what's
              running. Click routes to Platform → System where the
              existing Check/Upgrade UI can install it. Compact on
              medium screens (icon only), verbose at xl+. */}
          {autoUpdateAvail && autoLatest?.version && (
            <Link
              to="/platform/system"
              data-testid="header-update-available-pill"
              title={t("platformShell.updateAvailableTitle", {
                version: autoLatest.version,
                defaultValue: "Update available: v{{version}}",
              })}
              className="hidden md:inline-flex shrink-0 items-center gap-1.5 rounded-md border border-sky-500/50 bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:bg-sky-500/20 px-2 py-1 text-xs font-medium transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden xl:inline">
                {t("platformShell.updateAvailablePill", {
                  version: autoLatest.version,
                  defaultValue: "v{{version}} available",
                })}
              </span>
              <span className="xl:hidden font-mono">
                {String(autoLatest.version).slice(0, 12)}
              </span>
            </Link>
          )}
          {/* Right-side secondary controls. Hidden until xl (1280px) so
              iPad portrait (1024px) keeps logout/exit always visible. */}
          <div className="hidden xl:block shrink-0">
            <SetupGuideButton />
          </div>
          <div className="hidden xl:block shrink-0">
            <HelpModeToggle />
          </div>
          <div className="hidden xl:block shrink-0">
            <LanguageSwitcher />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle"
            className="shrink-0"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPwOpen(true)}
            data-testid="platform-change-password-btn"
            title="Change password"
            className="shrink-0 hidden xl:inline-flex"
          >
            <KeyRound className="h-4 w-4" />
          </Button>
          <div className="hidden xl:flex text-xs text-muted-foreground pl-2 border-l border-border items-center shrink-0">
            <div className="font-medium text-foreground truncate max-w-[120px]">
              {username || "admin"}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            data-testid="platform-logout"
            className="shrink-0"
            aria-label={t("nav.logout", "Logout")}
            title={t("nav.logout", "Logout")}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        <main className="px-3 sm:px-4 md:px-6 py-4 sm:py-6 max-w-[1600px]">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} forced={forced} />
    </div>
  );
}
